import {
  loadStripe,
  type PaymentMethod,
  type Stripe,
  type StripeElements,
  type PaymentRequestCompleteStatus,
} from '@stripe/stripe-js';

import { getClientIdWithTimeout } from '../analytics';
import { env } from '../env';
import type { UserProfile } from '../api/schema/profileSchema';
import { type CreateSubscriptionParams, sendCreateSubscriptionRequest } from '../api/payment';
import { ErrorPaymentHandler } from './errorHandlers';
import {
  createPaymentMethod,
  createPaymentIntent,
  createPaymentIntentSetup,
  handleCardAction,
  handleSetupAction,
  type OneOfPaymentMethods,
  prepareSubscriptionMetadata,
  prepateSubscriptioSuccessUrl,
  paymentRequestCompleteStatuses,
  paymentMethodTypes,
  type BillingInfo,
  type RequiredBillingInfo,
} from './paymentApi';

import type { Plan } from '@/utils/mapProductObject';

import AnalyticsEventHandler from '@/services/analytics/AnalyticsEventHandler';

interface SubmitData {
  getSelectedPlan: () => Plan | undefined;
  getUserProfile: () => UserProfile | undefined;
  getCouponCode: () => string | undefined;
}

interface SubmitActions {
  onStartSubmit: () => void;
  onStopSubmit: () => void;
  onSuccess: (profile: UserProfile) => void;
  showError: (text: string) => void;
}

export const createPaymentSubmit = (data: SubmitData, actions: SubmitActions) => {
  const {
    rethrowIfRequired,
    catchPlug,
    handlePaymentIntentError,
    handleErrorCardAction,
    handlePaymentMethodError,
    handleStripeLoading,
    throwStripeUnavailable,
  } = new ErrorPaymentHandler(actions.showError, actions.onStopSubmit);

  const stripePromise = loadStripe(env.stripeKey).catch(handleStripeLoading);

  const completeSuccessfulPayment = (profile: UserProfile) => {
    actions.onSuccess(profile);
    actions.onStopSubmit();
  };

  const createSubscription = (params: CreateSubscriptionParams) =>
    getClientIdWithTimeout().then((cid) =>
      sendCreateSubscriptionRequest(params, prepateSubscriptioSuccessUrl(), {
        ...prepareSubscriptionMetadata(),
        cid,
      }).then((res) => {
        console.log('Creat sub response', res.data);

        return res.data.subscription_id;
      }),
    );

  const createIntentAndSubscription = (
    plan: Plan,
    paymentMethodId: string,
    paymentMethodType: string,
    profile: UserProfile,
  ): Promise<string> => {
    const { email: billingEmail } = profile;

    return (
      plan.hasTrial
        ? createPaymentIntentSetup(paymentMethodId, billingEmail, plan.id)
        : createPaymentIntent(paymentMethodId, billingEmail, plan.id)
    ).then(({ isActionRequired, paymentIntentId, intentClientSecret }) => {
      const createSubRequest = () =>
        createSubscription({
          stripe_payment_method_id: paymentMethodId,
          stripe_payment_intent_id: paymentIntentId,
          payment_method: paymentMethodType,
          product_id: plan.id,
          billing_email: billingEmail,
        }).catch(handlePaymentIntentError);

      if (isActionRequired) {
        return stripePromise.then((stripe) => {
          if (!stripe) {
            return throwStripeUnavailable();
          }

          return plan.hasTrial
            ? handleSetupAction(stripe, intentClientSecret, paymentMethodId, handleErrorCardAction)
                .catch(rethrowIfRequired)
                .then(createSubRequest)
            : handleCardAction(stripe, intentClientSecret, handleErrorCardAction)
                .catch(rethrowIfRequired)
                .then(createSubRequest);
        });
      }

      return createSubRequest();
    }, handlePaymentIntentError);
  };

  const performCardPayment = (
    plan: Plan,
    elements: StripeElements,
    profile: UserProfile,
    coupon?: string,
    billingInfo?: BillingInfo,
  ) => {
    return stripePromise
      .then((stripe) => {
        if (!stripe) {
          return throwStripeUnavailable();
        }

        return createPaymentMethod(stripe, elements, profile.email, billingInfo);
      })
      .then(
        (paymentMethodId: string) =>
          createIntentAndSubscription(plan, paymentMethodId, paymentMethodTypes.card, profile),
        handlePaymentMethodError,
      )
      .then((subscriptionId) => {
        AnalyticsEventHandler.trackPurchaseWithCard(plan, subscriptionId, coupon);
        completeSuccessfulPayment(profile);
      }, catchPlug);
  };

  const expressTypeToPaymentMethodType = (type: string): string => {
    if (type === 'apple_pay') return paymentMethodTypes.applePay;
    if (type === 'google_pay') return paymentMethodTypes.googlePay;

    return type;
  };

  const onExpressCheckoutConfirm = async (
    stripe: Stripe,
    elements: StripeElements,
    expressPaymentType: string,
    onPaymentFailed: () => void,
  ) => {
    const selectedPlan = data.getSelectedPlan();
    const profile = data.getUserProfile();

    if (!selectedPlan || !profile) {
      onPaymentFailed();
      return;
    }

    const { error: submitError } = await elements.submit();

    if (submitError) {
      onPaymentFailed();

      return;
    }

    const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({ elements });

    if (pmError || !paymentMethod) {
      onPaymentFailed();

      return;
    }

    const paymentMethodId = paymentMethod.id;
    const paymentMethodType = expressTypeToPaymentMethodType(expressPaymentType);

    actions.onStartSubmit();

    return createIntentAndSubscription(selectedPlan, paymentMethodId, paymentMethodType, profile)
      .then((subscriptionId) => {
        if (paymentMethodType === paymentMethodTypes.applePay) {
          AnalyticsEventHandler.trackPurchaseWithApplePay(selectedPlan, subscriptionId, data.getCouponCode());
        } else if (paymentMethodType === paymentMethodTypes.googlePay) {
          AnalyticsEventHandler.trackPurchaseWithGooglePay(selectedPlan, subscriptionId, data.getCouponCode());
        } else {
          AnalyticsEventHandler.trackPurchaseWithOtherMethods(
            paymentMethodType,
            selectedPlan,
            subscriptionId,
            data.getCouponCode(),
          );
        }

        completeSuccessfulPayment(profile);
      })
      .catch((err: unknown) => {
        onPaymentFailed();
        rethrowIfRequired(err);
      })
      .catch(catchPlug);
  };

  const onPaymentRequestSubmit = (
    paymentMethod: PaymentMethod,
    paymentMethodType: OneOfPaymentMethods,
    onComplete: (status: PaymentRequestCompleteStatus) => void,
  ) => {
    const selectedPlan = data.getSelectedPlan();

    if (!selectedPlan) {
      throw new Error('Plan is not selected');
    }

    const profile = data.getUserProfile();

    if (!profile) {
      throw new Error('Profile is not available');
    }

    actions.onStartSubmit();

    return createIntentAndSubscription(selectedPlan, paymentMethod.id, paymentMethodType, profile)
      .then(
        (subscriptionId) => {
          onComplete(paymentRequestCompleteStatuses.success);
          if (paymentMethodType === paymentMethodTypes.applePay) {
            AnalyticsEventHandler.trackPurchaseWithApplePay(selectedPlan, subscriptionId, data.getCouponCode());
          } else if (paymentMethodType === paymentMethodTypes.googlePay) {
            AnalyticsEventHandler.trackPurchaseWithGooglePay(selectedPlan, subscriptionId, data.getCouponCode());
          }

          completeSuccessfulPayment(profile);
        },
        (err: unknown) => {
          onComplete(paymentRequestCompleteStatuses.fail);
          rethrowIfRequired(err);
        },
      )
      .catch(catchPlug);
  };

  const onCardFormSubmit = (elements: StripeElements | null, billingInfo: RequiredBillingInfo) => {
    if (!elements) {
      throw new Error('Stripe Elements is not available');
    }

    const selectedPlan = data.getSelectedPlan();

    if (!selectedPlan) {
      throw new Error('Plan is not selected');
    }
    const profile = data.getUserProfile();

    if (!profile) {
      throw new Error('Profile is not available');
    }

    AnalyticsEventHandler.trackCheckoutWithCard(selectedPlan);
    actions.onStartSubmit();

    return performCardPayment(selectedPlan, elements, profile, data.getCouponCode(), billingInfo);
  };

  return { stripePromise, onPaymentRequestSubmit, onCardFormSubmit, onExpressCheckoutConfirm };
};
